import { useEffect } from 'react';

/**
 * Keeps the screen awake while `enabled` is true, using the Screen Wake Lock API.
 *
 * The browser drops the lock whenever the tab is hidden, so it is requested again each time
 * the page becomes visible. A refused request (low battery, power saving) is not an error the
 * user can act on, so it is ignored and the screen simply sleeps as usual.
 */
export function useWakeLock(enabled: boolean): { supported: boolean } {
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!enabled || !supported) return;
    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    async function acquire() {
      if (document.visibilityState !== 'visible') return;
      try {
        const next = await navigator.wakeLock.request('screen');
        // The effect may have been cleaned up while the request was in flight.
        if (released) void next.release();
        else sentinel = next;
      } catch {
        // Refused; the screen sleeps as normal.
      }
    }

    function onVisibilityChange() {
      if (sentinel == null || sentinel.released) void acquire();
    }

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void sentinel?.release();
    };
  }, [enabled, supported]);

  return { supported };
}
